import { makeStyles } from "@mui/styles";
import { colors } from "../../styles/colors";
import { down, hoverOnly, reducedMotion } from "../../styles/breakpoints";
import { pageShellRoot, pageContentRootWide, fullBleedOnMobile } from "../../styles/pageShell";

/**
 * Pub page styles.
 *
 * This file previously declared its own breakpoint ladder (600 / 960 / 1200 /
 * 1280), none of which matched the `md: 900` width at which the sidebar turns
 * into an overlay drawer. So the page reflowed to its "tablet" layout at 960px
 * while the sidebar was still a rail, and again at 900px when the sidebar
 * changed — two separate jumps. Everything below uses the shared tokens.
 */
const useStyles = makeStyles((_) => ({
  /* Outer shell. The sidebar owns the height and the scrolling; the extra
     `minHeight: 100vh` here produced a second scroll container. */
  contentMain: pageShellRoot,

  /*
   * This class is merged onto the <main> element, which carries an inline
   * `margin-left` equal to the sidebar width. Declaring a width here resolved
   * against the shell's content box (the full viewport) and the margin was
   * then ADDED to it, making the page 100vw + sidebar wide. The shell clips
   * overflow, so the excess was cut off and unreachable -- the last KPI card
   * and the header action button simply vanished, and the clipped region grew
   * when the sidebar was expanded.
   *
   * <main> is a stretched flex item: with no width declared it already fills
   * the space left over by the margin. An earlier attempt at
   * `width: calc(100% - 64px)` was a related mistake -- under the global
   * `box-sizing: border-box` the padding is already inside the declared
   * width, so it also threw away 64px of usable space.
   */
  pubRoot: pageContentRootWide,

  contentWrapper: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    position: "relative",
  },

  /* ----------------------------------------------------------------------
     Header
     ---------------------------------------------------------------------- */

  containerHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    marginBottom: "24px",
    gap: "16px",
    position: "relative",
    zIndex: "var(--z-content)",

    [down.md]: {
      marginBottom: "20px",
      flexDirection: "column",
      alignItems: "stretch",
    },
    [down.sm]: {
      marginBottom: "16px",
      gap: "12px",
    },
  },

  iconCircle: {
    width: "56px",
    height: "56px",
    borderRadius: "12px",
    backgroundColor: colors.primary,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 4px 12px rgba(8, 152, 255, 0.3)",
    flexShrink: 0,

    [down.md]: {
      width: "48px",
      height: "48px",
      borderRadius: "10px",
    },
    [down.sm]: {
      width: "44px",
      height: "44px",
      borderRadius: "8px",
    },
  },

  divIconArrow: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    flex: "1 1 auto",
    /* Was a flat `minWidth: 200px`, which on a 320px phone left almost no
       room beside the 44px icon. `min()` lets it give way when it must. */
    minWidth: "min(200px, 100%)",

    "& h2": {
      color: colors.BLACK,
      /* Four media queries stepped this 28 → 24 → 22 → 20. One clamp covers
         the same range and scales smoothly between the steps. */
      fontSize: "clamp(20px, 2.2vw + 12px, 28px)",
      fontWeight: 700,
      margin: 0,
      letterSpacing: "0.5px",
      minWidth: 0,
      overflowWrap: "anywhere",
    },
  },

  /* ----------------------------------------------------------------------
     Stats
     ---------------------------------------------------------------------- */

  /*
   * The five KPI cards used md={2.4} alone, i.e. five per row from 900px
   * upward with no intermediate step. Between 900px and 1200px each card was
   * left roughly 150-190px to hold a label, a large figure AND a 48px avatar,
   * so the row overflowed and the last card was clipped at the right edge.
   * Pub.tsx now steps 1 -> 2 -> 3 -> 5 (xs / sm / md / lg); minWidth: 0 here
   * lets the grid items shrink instead of forcing the track wider than the
   * page.
   */
  statsContainer: {
    minWidth: 0,
    marginBottom: "24px",
    [down.md]: {
      marginBottom: "20px",
    },
    [down.sm]: {
      marginBottom: "16px",
    },
  },

  statCard: {
    height: "100%",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
    /* `transition: all` also animates layout properties, which makes the card
       jitter whenever the grid reflows. Only the two animated properties are
       listed now. */
    transition: "transform 0.3s ease, box-shadow 0.3s ease",
    border: `1px solid ${colors.default}`,

    /* The lift was applied on every device. On a touch screen the hover state
       sticks after a tap, leaving one card permanently raised. */
    [hoverOnly]: {
      "&:hover": {
        transform: "translateY(-4px)",
        boxShadow: "0 6px 20px rgba(0, 0, 0, 0.12)",
      },
    },
    [reducedMotion]: {
      transition: "none",
    },

    "& .MuiCardContent-root": {
      padding: "20px",
      "&:last-child": {
        paddingBottom: "20px",
      },
      [down.sm]: {
        padding: "16px",
        "&:last-child": {
          paddingBottom: "16px",
        },
      },
    },
  },

  /* ----------------------------------------------------------------------
     Filters
     ---------------------------------------------------------------------- */

  filterControls: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    flexWrap: "wrap",
    gap: "16px",
    marginBottom: "24px",
    padding: "20px",
    backgroundColor: colors.WHITE,
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
    border: `1px solid ${colors.default}`,
    position: "relative",
    /* Was a bare `zIndex: 100`, invented to lift the select dropdowns above
       the table. Dropdowns are portalled to the body and handled by
       `--z-overlay` in index.css, so this only needs to beat the table. */
    zIndex: "var(--z-sticky-header)",

    [down.md]: {
      flexDirection: "column",
      alignItems: "stretch",
      gap: "12px",
      marginBottom: "20px",
      padding: "16px",
    },
    [down.sm]: {
      marginBottom: "16px",
      padding: "12px",
      borderRadius: "8px",
    },
  },

  filterGroup: {
    display: "flex",
    gap: "16px",
    alignItems: "center",
    flex: "1 1 auto",
    flexWrap: "wrap",
    minWidth: 0,

    [down.md]: {
      gap: "12px",
      width: "100%",
    },
    [down.sm]: {
      flexDirection: "column",
      alignItems: "stretch",
      gap: "10px",
    },
  },

  filterControl: {
    minWidth: "200px",
    flex: "1 1 200px",
    maxWidth: "300px",
    position: "relative",
    zIndex: "inherit",

    [down.lg]: {
      minWidth: "180px",
      flex: "1 1 180px",
    },
    [down.md]: {
      minWidth: "150px",
      flex: "1 1 auto",
      maxWidth: "none",
    },
    [down.sm]: {
      minWidth: "100%",
      width: "100%",
      maxWidth: "none",
    },

    "& .MuiOutlinedInput-root": {
      backgroundColor: colors.WHITE,
      borderRadius: "8px",

      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: colors.primary,
      },

      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: colors.primary,
        borderWidth: "2px",
      },
    },

    "& .MuiInputLabel-root": {
      color: colors.greydark,
      fontSize: "14px",

      "&.Mui-focused": {
        color: colors.primary,
        fontWeight: 500,
      },
    },

    "& .MuiPaper-root": {
      marginTop: "8px",
      maxHeight: "300px",
      borderRadius: "8px",
      boxShadow: "0 4px 16px rgba(0, 0, 0, 0.15)",
    },
  },

  clearFiltersWrapper: {
    display: "flex",
    alignItems: "center",
    flexShrink: 0,

    [down.sm]: {
      width: "100%",
      marginTop: "4px",

      "& button": {
        width: "100%",
        justifyContent: "center",
      },
    },
  },

  /* ----------------------------------------------------------------------
     Table
     ---------------------------------------------------------------------- */

  tableContainer: {
    backgroundColor: colors.WHITE,
    borderRadius: "12px",
    overflow: "auto",
    WebkitOverflowScrolling: "touch",
    boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
    border: `1px solid ${colors.default}`,
    position: "relative",
    zIndex: "var(--z-content)",
    minWidth: 0,

    [down.md]: {
      borderRadius: "8px",
    },
    [down.sm]: {
      /* Full-bleed on a phone: the card cancels the page gutter so the table
         gets the entire screen width to scroll within. Both the gutter and
         these offsets now derive from PAGE_GUTTER_SM, so they cannot drift
         apart again (they were once 12px against a 16px gutter). */
      ...fullBleedOnMobile,
      boxShadow: "0 1px 4px rgba(0, 0, 0, 0.08)",
    },

    "& table": {
      width: "100%",
      tableLayout: "auto",
    },

    /* Keeps the columns readable and scrolls horizontally rather than
       crushing them; the container above is the scroll port.

       Was 1000px, which forced a horizontal scrollbar on a 1280px laptop with
       the sidebar expanded: 1280 - 240 sidebar - 64 gutter = 976px of usable
       width, just under the floor. 900px clears that case while still keeping
       eight columns legible. */
    "& .MuiTable-root": {
      minWidth: "900px",
    },

    "& thead": {
      backgroundColor: colors.bgcolor,

      "& th": {
        fontWeight: 600,
        fontSize: "13px",
        textTransform: "uppercase",
        color: colors.BLACK,
        letterSpacing: "0.5px",
        borderBottom: `2px solid ${colors.default}`,
        padding: "12px 16px",
        whiteSpace: "nowrap",
      },
    },

    "& tbody": {
      "& tr": {
        transition: "background-color 0.2s",
        [reducedMotion]: {
          transition: "none",
        },
        [hoverOnly]: {
          "&:hover": {
            backgroundColor: colors.lightBlue,
          },
        },
      },

      "& td": {
        fontSize: "14px",
        color: colors.BLACK,
        borderBottom: `1px solid ${colors.default}`,
        padding: "12px 16px",
      },
    },
  },

  addButton: {
    flexShrink: 0,
    whiteSpace: "nowrap",

    [down.md]: {
      width: "100%",
      "& button": {
        width: "100%",
        justifyContent: "center",
      },
    },
  },
}));

export default useStyles;
